import React, { useContext } from "react";
import { CoreRow } from "@tanstack/react-table";
import { RESOURCE_NAME } from "common/authorization";
import { buildLink } from "common/routing";
import { formatDocumentUploadName } from "common-client/utils/documentUploads";
import { colors } from "../../../stitches.config";
import { AuthContext } from "../../Authorization/AuthContext";
import { useEditTaskModal } from "../../Tasks/Form/EditTaskForm";
import { ExpandableCell } from "../ExpandableCell";
import { IconNames } from "../Icons/LucideIcons";

export type TaskForCell = {
  name: string;
  id: string;
};

export const filesCell = ({
  row,
  isSubmission,
}: {
  row: CoreRow<any>["original"];
  isSubmission?: boolean;
}): Maybe<JSX.Element> => {
  const { authorized } = useContext(AuthContext);
  const canViewDocumentDetail = authorized({
    resource: RESOURCE_NAME.DOCUMENT_UPLOAD,
    permission: "view_details",
  });

  const pills = row?.map(
    (f: { issuedAt: string; createdAt: string; type: string; id: string }) => {
      let href;

      if (canViewDocumentDetail) {
        href = buildLink(
          "documentUploadDetail",
          {
            id: f.id,
          },
          {},
        );
      } else {
        href = buildLink("documentUploadFile", { id: f.id }, {});
      }

      return {
        label: formatDocumentUploadName({
          isSubmission,
          createdAt: f.createdAt,
          issuedAt: f.issuedAt,
          accountDocumentTypeName: f.type,
        }),
        iconName: "file-text",
        href,
      };
    },
  );

  return <ExpandableCell pills={pills} />;
};

export const pillsCell = ({
  row,
  iconName,
  color,
  iconColor,
  clickable = true,
}: {
  row: CoreRow<any>["original"];
  iconName?: IconNames;
  color?: keyof typeof colors;
  iconColor?: keyof typeof colors;
  clickable?: boolean;
}): Maybe<JSX.Element> => {
  const pills = row?.map((pill: string) => ({
    label: pill,
    iconName,
    color,
    iconColor,
  }));

  return <ExpandableCell pills={pills} clickable={clickable} />;
};

export const TaskPill = ({ task }: { task: TaskForCell }) => {
  const [showTaskModal, hideTaskModal] = useEditTaskModal({
    taskId: task.id,
    onSuccess: () => {
      hideTaskModal();
    },
  });

  return {
    label: task.name,
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      showTaskModal();
    },
  };
};

export const TasksCell = ({ tasks }: { tasks: Array<TaskForCell> }) => {
  if (tasks.length === 0) {
    return null;
  }

  const pills = tasks.map((task: { name: string; id: string }) =>
    TaskPill({ task }),
  );

  return <ExpandableCell pills={pills} />;
};
